import React, { useState, useEffect, useRef } from "react";
import { IInputFilesModalProps, UploadedDocument } from "./definitions";
import { FileMetadata } from "../input-files/definitions";
import styles from "./style.module.scss";
import InputFiles from "components/input-files/InputFiles";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import Box from "@amzn/awsui-components-react/polaris/box";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import ProgressBar from "@amzn/awsui-components-react/polaris/progress-bar";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Icon from "@amzn/awsui-components-react/polaris/icon";
import { useAppSelector } from "app/store/hooks";
import {
  validateFileMetadata,
  MAX_FILES_PER_GROUP,
} from "utils/input-files-utils";
import { extractUniqueKey } from "utils/document-utils";
import {
  getFileExtension,
  getFileNameWithoutExtension,
} from "utils/date-format";
import {
  checkBatchVersionCollisionsOS,
  renderVersionCollisionMessageOS,
  VersionCollisionInfoOS,
} from "../documents-table-os/version-utils-os";
import {
  createInitialUploadProgress,
  UploadProgress,
  CancelToken,
} from "utils/upload-progress-utils";
import { Document } from "models/schemas";

const InputFilesModal: React.FC<IInputFilesModalProps> = (
  props: IInputFilesModalProps,
) => {
  const teamName: string = useAppSelector((state) => state.audits.teamName);
  const [files, setFiles] = useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [replaceExistingMap, setReplaceExistingMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [duplicateFiles, setDuplicateFiles] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(
    createInitialUploadProgress(),
  );
  const cancelTokenRef = useRef<CancelToken>({ cancelled: false });
  const progressBarRef = useRef<HTMLDivElement>(null);

  // OpenSearch collision detection state
  const [showOSCollisionWarning, setShowOSCollisionWarning] = useState(false);
  const [osCollisions, setOSCollisions] = useState<VersionCollisionInfoOS[]>(
    [],
  );
  const [osCollisionAcknowledged, setOSCollisionAcknowledged] = useState(false);
  const [isCheckingCollisions, setIsCheckingCollisions] = useState(false);
  const [answersKBChecked, setAnswersKBChecked] = useState(false);

  // Auto-scroll to progress bar when upload starts
  useEffect(() => {
    if (uploadProgress.isUploading && progressBarRef.current) {
      progressBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [uploadProgress.isUploading]);

  // Version collision detection via OpenSearch is always used now

  // Duplicate detection: compare file names against existing documents
  useEffect(() => {
    // Only run when enableReplaceMode is true
    if (!props.enableReplaceMode || !props.existingDocuments) {
      return;
    }

    const duplicates = new Set<string>();

    // Compare each file name against existing documents (case-sensitive exact match)
    files.forEach((file) => {
      const isDuplicate = props.existingDocuments!.some((doc) => {
        const fullFileName = doc.fileName + (doc.extensionType || "");
        return (
          fullFileName === file.name &&
          extractUniqueKey(doc.documentId) !== null
        );
      });
      if (isDuplicate) {
        duplicates.add(file.name);
      }
    });

    setDuplicateFiles(duplicates);

    setReplaceExistingMap((prev) => {
      const next = new Map<string, boolean>();
      for (const fileName of duplicates) {
        next.set(fileName, prev.get(fileName) ?? true);
      }
      return next;
    });
  }, [files, props.existingDocuments, props.enableReplaceMode]);

  const onDismiss = (e: any) => {
    // Prevent closing modal during upload
    if (uploadProgress.isUploading) {
      // Use global notification system so it appears outside the modal
      if (props.addNotification) {
        props.addNotification({
          type: "info",
          content: "Upload in progress. Please wait or click Cancel to abort.",
          dismissible: true,
          timeout: 3000,
        });
      }
      return;
    }
    if (e.detail.reason === "overlay") return;
    resetModal();
  };

  const onCancelSave = () => {
    if (uploadProgress.isUploading) {
      // Set cancel flag to abort ongoing upload
      cancelTokenRef.current.cancelled = true;
      // The upload function will handle cleanup and show notification
    } else {
      resetModal();
    }
  };

  const onCreate = async (_e: any) => {
    // Validate files and metadata based on enabled features
    if (props.enableRenaming || props.enableDocumentTypes) {
      const validation = validateFileMetadata(
        fileMetadata,
        props.requireDocumentTypes,
        props.enableGroups ? MAX_FILES_PER_GROUP : undefined,
      );

      if (!validation.isValid) {
        setFileError(true);
        return;
      }
    } else {
      if (files.length === 0) {
        setFileError(true);
        return;
      }
    }

    // OpenSearch collision detection — runs for all upload paths
    if (
      props.searchDocuments &&
      !showOSCollisionWarning &&
      props.versioningEnabled
    ) {
      setIsCheckingCollisions(true);
      try {
        // Convert files to temp documents for collision checking
        const tempDocs: Document[] = files.map((file, index) => {
          const extension = getFileExtension(file.name);
          const baseName = getFileNameWithoutExtension(file.name);

          return {
            documentId: "",
            fileName: fileMetadata[index]?.fileName || baseName,
            extensionType: extension,
            documentType:
              fileMetadata[index]?.documentType ||
              props.defaultDocumentType ||
              "",
            parentId: props.parentId || "",
            teamName,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString(),
            createdBy: "",
            modifiedBy: "",
            size: `${file.size} bytes`,
            isFolder: false,
            deleted: false,
            securityLevel: "Confidential",
          };
        });

        const collisions = await checkBatchVersionCollisionsOS(
          tempDocs,
          () => ({}), // empty object = new document
          props.searchDocuments,
        );

        setIsCheckingCollisions(false);

        if (collisions.length > 0) {
          setOSCollisions(collisions);
          setShowOSCollisionWarning(true);
          return; // Stop here - show collision warning
        }
      } catch (error) {
        console.error("Error checking collisions:", error);
        setIsCheckingCollisions(false);
        // Proceed with upload even if collision check fails
      }
    }

    // Proceed with upload (no collisions OR after confirmation)
    setIsDisabled(true);
    // Reset cancel token for new upload
    cancelTokenRef.current = { cancelled: false };
    if (props.enableRenaming || props.enableDocumentTypes) {
      if (props.onUpload) {
        props.onUpload(
          fileMetadata,
          (uploadedDocs?: UploadedDocument[]) => {
            if (uploadedDocs?.length && answersKBChecked && props.onKBIngest) {
              props.onKBIngest(uploadedDocs);
            }
            resetModal();
          },
          (progress: UploadProgress) => {
            setUploadProgress(progress);
          },
          cancelTokenRef.current,
        );
      }
    } else {
      if (props.onCreate) {
        props.onCreate(
          files,
          teamName,
          (uploadedDocs?: UploadedDocument[]) => {
            if (uploadedDocs?.length && answersKBChecked && props.onKBIngest) {
              props.onKBIngest(uploadedDocs);
            }
            resetModal();
          },
          replaceExistingMap,
          props.existingDocuments || [],
          (progress: UploadProgress) => {
            setUploadProgress(progress);
          },
          cancelTokenRef.current,
        );
      }
    }
  };

  const resetModal = () => {
    props.setVisible(false);
    setFiles([]);
    setFileMetadata([]);
    setFileError(false);
    setIsDisabled(false);
    setReplaceExistingMap(new Map());
    setDuplicateFiles(new Set());
    setUploadProgress(createInitialUploadProgress());
    cancelTokenRef.current = { cancelled: false };
    // Reset OpenSearch collision state
    setShowOSCollisionWarning(false);
    setOSCollisions([]);
    setOSCollisionAcknowledged(false);
    setIsCheckingCollisions(false);
    setAnswersKBChecked(false);
  };

  const handleFileMetadataChange = (metadata: FileMetadata[]) => {
    setFileMetadata(metadata);
    setFileError(false);
  };

  const getErrorMessage = () => {
    if (!fileError) return undefined;
    if (props.enableRenaming || props.enableDocumentTypes) {
      const validation = validateFileMetadata(
        fileMetadata,
        props.requireDocumentTypes,
        props.enableGroups ? MAX_FILES_PER_GROUP : undefined,
      );
      return validation.errorMessage;
    }
    return "Please select files";
  };

  return (
    <Modal
      visible={props.visible}
      onDismiss={onDismiss}
      header={props.title || "Upload files"}
      data-testid="input-files-modal"
      size="large"
    >
      <div className={styles.modalContent}>
        <div className={styles.modalBody}>
          {props.description && (
            <div>
              {props.description}
              <br />
              <br />
            </div>
          )}

          {props.enableGroups && fileError && getErrorMessage() && (
            <Box margin={{ bottom: "s" }}>
              <Alert type="error">{getErrorMessage()}</Alert>
            </Box>
          )}

          <InputFiles
            setFiles={setFiles}
            files={files}
            enableRenaming={props.enableRenaming}
            enableDocumentTypes={props.enableDocumentTypes}
            requireDocumentTypes={props.requireDocumentTypes}
            enableGroups={props.enableGroups}
            onFileMetadataChange={handleFileMetadataChange}
            fileMetadata={fileMetadata}
            acceptedFileTypes={props.acceptedFileTypes}
            documentTypeOptions={props.documentTypeOptions}
            defaultDocumentType={props.defaultDocumentType}
            gaiaObjectType={props.gaiaObjectType}
            isUploading={uploadProgress.isUploading}
            multiple={props.multiple}
            showReplaceColumn={!!props.enableReplaceMode}
            duplicateFiles={duplicateFiles}
            replaceExistingMap={replaceExistingMap}
            onReplaceExistingChange={(fileName: string, checked: boolean) => {
              setReplaceExistingMap((prev) => {
                const next = new Map(prev);
                next.set(fileName, checked);
                return next;
              });
            }}
          />

          {showOSCollisionWarning && osCollisions.length > 0 && (
            <div className={styles.sectionSpacing}>
              <Container
                header={
                  <Header variant="h3">
                    <span className={styles.warningText}>
                      <Icon name="status-warning" /> Version Collision Warning
                    </span>
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {renderVersionCollisionMessageOS(osCollisions)}
                  <Checkbox
                    checked={osCollisionAcknowledged}
                    onChange={({ detail }) =>
                      setOSCollisionAcknowledged(detail.checked)
                    }
                  >
                    I understand these files will become versions of existing
                    files
                  </Checkbox>
                </SpaceBetween>
              </Container>
            </div>
          )}

          {props.showAnswersKB && (
            <div className={styles.kbSection}>
              <SpaceBetween size="s">
                <h3 className={styles.kbSectionHeader}>
                  Add to knowledge base
                </h3>
                <Alert type="info">
                  Only add final documents to the knowledge base. Draft files
                  are not recommended as they may contain incomplete or
                  inaccurate information.
                </Alert>
                <Checkbox
                  checked={answersKBChecked}
                  onChange={({ detail }) => setAnswersKBChecked(detail.checked)}
                >
                  Answers knowledge base
                </Checkbox>
              </SpaceBetween>
            </div>
          )}

          {!props.enableGroups && fileError && (
            <div className={styles.errorMessage}>{getErrorMessage()}</div>
          )}

          {uploadProgress.isUploading && (
            <div ref={progressBarRef}>
              <Box margin={{ top: "m" }}>
                <ProgressBar
                  value={uploadProgress.currentFileProgress}
                  label={`Uploading ${uploadProgress.currentFileIndex} of ${uploadProgress.totalFiles}: ${uploadProgress.currentFileName}`}
                  status={
                    uploadProgress.cancelRequested ? "error" : "in-progress"
                  }
                />
              </Box>
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <Box>
            <SpaceBetween direction="horizontal" size="xs">
              <Button formAction="none" onClick={onCancelSave} variant="link">
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={
                  isDisabled ||
                  isCheckingCollisions ||
                  (files.length === 0 && fileMetadata.length === 0) ||
                  (showOSCollisionWarning && !osCollisionAcknowledged)
                }
                onClick={onCreate}
              >
                {isDisabled || isCheckingCollisions ? <Spinner /> : null}
                {isCheckingCollisions
                  ? "Checking..."
                  : showOSCollisionWarning
                    ? "Confirm and Upload"
                    : "Upload"}
              </Button>
            </SpaceBetween>
          </Box>
        </div>
      </div>
    </Modal>
  );
};

export default InputFilesModal;
